import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Heart, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { setToken, removeToken } from '../utils/auth';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    // Drop any stale/expired token so the session exchange starts clean
    removeToken();
    // CRITICAL: Use window.location.origin dynamically - no hardcoding or fallbacks
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Restoring this page from the back/forward cache keeps React state, which would
  // leave the Google button stuck in its disabled "Connecting..." state.
  useEffect(() => {
    const resetGoogleButton = () => setGoogleLoading(false);
    window.addEventListener('pageshow', resetGoogleButton);
    return () => window.removeEventListener('pageshow', resetGoogleButton);
  }, []);

  const validate = () => {
    const newErrors = {};
    
    if (!formData.email.includes('@')) {
      newErrors.email = 'Invalid email format';
    }
    
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be 8+ characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      const response = await api.post('/auth/signup', {
        email: formData.email,
        password: formData.password
      });
      
      setToken(response.data.token);
      toast.success('Account created! Please verify your email.');
      
      // Redirect to email verification - code is sent via email only
      navigate('/verify-email', { 
        state: { 
          email: formData.email
        }
      });
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many signup attempts. Try again in an hour.');
      } else if (error.response?.status === 409) {
        toast.error('User with this Email ID already exists');
      } else {
        toast.error(error.response?.data?.detail || 'Signup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-8 min-h-screen flex flex-col">
        {/* Header */}
        <div className="text-center mb-8">
          <Heart className="text-primary fill-primary mx-auto mb-2 pulse-animation" size={40} />
          <h1 className="text-3xl font-bold text-white">Create Account</h1>
          <p className="text-gray-400 text-sm mt-2">Join BlindMatch - Find real connections</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 flex-1">
          {/* Email */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              placeholder="you@example.com"
              data-testid="signup-email-input"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1" data-testid="signup-email-error">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              placeholder="Minimum 8 characters"
              data-testid="signup-password-input"
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1" data-testid="signup-password-error">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Confirm Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              placeholder="Re-enter password"
              data-testid="signup-confirm-password-input"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1" data-testid="signup-confirm-password-error">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="signup-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Creating Account...
              </>
            ) : (
              'Create Account & Continue'
            )}
          </button>
          
          {/* Divider */}
          <div className="flex items-center gap-4 my-2">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>
          
          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
            data-testid="google-signup-button"
          >
            {googleLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Connecting...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-8 text-center">
          <Link to="/login" className="text-gray-400 hover:text-white transition-colors" data-testid="signup-login-link">
            Already have account? Log in
          </Link>
        </div>
      </div>
    </div>
  );
}
